import { Globe } from "@phosphor-icons/react";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { supabase } from "@/lib/supabase";

interface FaviconProps {
  src?: string | null;              // Original URL (fallback)
  storagePath?: string | null;      // Supabase Storage path (preferred)
  alt?: string;
  className?: string;
  url?: string;                     // For Google fallback
  reset?: boolean;
}

export function Favicon({ src, storagePath, alt, className, url, reset }: FaviconProps) {
  const [hasError, setHasError] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  // Update image URL when props change
  useEffect(() => {
    setHasError(false);
    
    // Prefer storage path if available
    if (storagePath) {
      const { data } = supabase.storage
        .from('webpage-assets')
        .getPublicUrl(storagePath);
      setImageUrl(data.publicUrl);
    } else if (src) {
      setImageUrl(src);
    } else {
      setImageUrl(null);
    }
  }, [src, storagePath, reset]);

  const handleError = () => {
    // If storage path failed, try original URL
    if (storagePath && src && imageUrl !== src) {
      console.warn('[Favicon] Storage path failed, trying original:', storagePath);
      setImageUrl(src);
      return;
    }
    if (process.env.NODE_ENV === 'development') {
      console.warn('[Favicon] All sources failed:', { src, storagePath, url });
    }
    setHasError(true);
  };

  // Try to get domain for fallback
  const getFallbackFavicon = () => {
    if (!url) return null;
    try {
      const domain = new URL(url).hostname.replace('www.', '');
      return `https://www.google.com/s2/favicons?domain=${domain}&size=128`;
    } catch {
      return null;
    }
  };

  const fallbackSrc = getFallbackFavicon();

  // Show globe icon if no valid favicon source is available
  if ((!imageUrl && !fallbackSrc) || hasError) {
    return (
      <div className={cn("flex items-center justify-center rounded-sm bg-neutral-100", className)}>
        <Globe className="h-3 w-3 text-neutral-400" weight="bold" />
      </div>
    );
  }

  // Show favicon from storage, database, or fallback
  return (
    <div className={cn("relative rounded-sm overflow-hidden", className)}>
      <img
        src={imageUrl || fallbackSrc || ''}
        alt={alt || "Website favicon"}
        className="h-full w-full object-contain"
        onError={handleError}
      />
    </div>
  );
}
